import { SelectItemType } from '../components/select-with-search/select-item.type';
import { replace } from '../helpers/array.helper';
import { DateRange } from '../types/date-range.type';
import { FilteringHelper } from './filtering.helper';

export enum FilterName {
  Category = 'category',
  Domain = 'domain',
  EntityName = 'entityName',
  Exception = 'exception',
  Feedback = 'feedback',
  Impact = 'impact',
  IsDeleted = 'is-deleted',
  Metric = 'metric',
  MonitoringFolder = 'monitoring-folder',
  Process = 'process',
  PropertyName = 'propertyName',
  Queue = 'queue',
  Resource = 'resource',
  Search = 'search',
  Status = 'status',
  Tags = 'tags',
  Tenant = 'tenant',
  TimePeriod = 'time-period',
  User = 'user',
  UtilizationInterval = 'utilization-interval',
}

export const TagFilter = {
  noTag: { id: 'noTag', label: 'Not tagged', fixed: true, type: SelectItemType.Explicit } as FilterFixedValue,
  all: { id: 'all', label: 'Any tag', fixed: true, type: SelectItemType.All } as FilterFixedValue,
};

export interface FilterItemValue {
  id: any;
  label: string;
  count?: number;
  disabled?: boolean;
  group?: string;
  description?: string;
}

export interface FilterFixedValue {
  id: string;
  label: string;
  type: SelectItemType;
  count?: number;
  disabled?: boolean;
}

export enum FilterItemType {
  Simple,
  Select,
  MultiSelect,
}

export interface BaseFilterItem {
  type: FilterItemType;
  name: string;
  queryParamName?: string;
  getTooltip?: () => string;
}

export interface SimpleFilterItem extends BaseFilterItem {
  type: FilterItemType.Simple;
  value?: any;
  defaultValue?: any;
}

export interface SelectFilterItem extends BaseFilterItem {
  type: FilterItemType.Select;
  value?: any;
  defaultValue?: any;
  items: FilterItemValue[];
  fixedItems?: FilterFixedValue[];
  placeholder?: string;
}

export interface MultiSelectFilterItem extends BaseFilterItem {
  type: FilterItemType.MultiSelect;
  value?: any[];
  defaultValue?: any[];
  items: FilterItemValue[];
  fixedItems?: FilterFixedValue[];
  placeholder?: string;
  clearFilterIfAllSelected?: boolean;
}

export type FilterItem = SimpleFilterItem | SelectFilterItem | MultiSelectFilterItem;

export class FilterDefinition {
  constructor(public filters: FilterItem[]) {}

  isFilterActive(name: string): boolean {
    return this.filters.find(filter => filter.name === name)?.value != null;
  }

  isAnyFilterActive(names: string[]): boolean {
    return names?.some(name => this.isFilterActive(name));
  }

  getFilterValue<T extends boolean | string | string[] | DateRange | null>(name: string): T {
    const filter = this.filters.find(f => f.name === name);
    if (filter == null) {
      return null;
    }

    return filter.value as T;
  }

  getFilterValues(names: string[]): any {
    return names.reduce((acc, name) => ({ ...acc, [name]: this.getFilterValue(name) }), {});
  }

  getFilter(name: string): FilterItem | null {
    return this.filters.find(f => f.name === name);
  }

  updateFilter(name: string, filter: FilterItem): void {
    const oldFilter = this.getFilter(name);
    replace(this.filters, oldFilter, filter);
  }

  static createSelect(name: string, items: FilterItemValue[], allItemsLabel: string, value: any, queryParamName: string = null, placeholder: string = null, getTooltip?: () => string): SelectFilterItem {
    const defaultValue = allItemsLabel != null ? null : items[0]?.id;
    return this.createFilterItem(FilterItemType.Select, name, items, allItemsLabel, value, defaultValue, queryParamName, placeholder, getTooltip) as SelectFilterItem;
  }

  static createMultiSelect(name: string, items: FilterItemValue[], allItemsLabel: string, value: any[], queryParamName: string = null, placeholder: string = null): MultiSelectFilterItem {
    return this.createFilterItem(FilterItemType.MultiSelect, name, items, allItemsLabel, value, null, queryParamName, placeholder) as MultiSelectFilterItem;
  }

  static createFilterItem(
    type: FilterItemType,
    name: string,
    items: FilterItemValue[],
    allItemsLabel: string | null,
    value: any,
    defaultValue: any,
    queryParamName: string = null,
    placeholder: string = null,
    getTooltip?: () => string,
  ): FilterItem {
    const fixedItems = allItemsLabel != null ? [{ id: FilteringHelper.AllItemsId, label: allItemsLabel, type: SelectItemType.All, isFixed: true }] : [];
    return { type, name, items, fixedItems, value, defaultValue, queryParamName, placeholder, clearFilterIfAllSelected: true, getTooltip };
  }
}
